import { Attribute, Group, ImageItem, Option, ProductArea, Template, TemplateArea, TextItem } from 'zakeke-configurator-react';
import groupBy from 'lodash-es/groupBy';
import { CODE_SECTION, CODE_SPLIT_CHAR, FUTURE_FASHION_CONSTANTS } from '../../constants';
import {
  CameraInterface,
  ViewerGroupInterface,
  ViewerOptions,
  ViewerAttributeInterface,
  ViewerGroupAttributeInterface,
  ViewerGroupCustomizationInterface,
  ViewerOptionInterface,
  ViewerGroupStepInterface,
  ViewerItemInterface,
  ViewerTemplateAreaInterface,
  ViewerGroupInputItemsInterface,
  ViewerOptionInterfaceInput,
} from '../../models';
import { CustomizationAssets } from '../customizations';

type MapViewerOptionsCustomizationParamsType = {
  name: string | null;
  items: TextItem[];
  currentTemplate: Template | null;
  productAreas: ProductArea[];
};

export type ViewerSelectionItemArgs = {
  groups: {
    groupId: number;
    visibleAttributes: number[];
  }[];
};

export type MapViewerOptionsParamsType = {
  groups: Group[] | null;
  customization: MapViewerOptionsCustomizationParamsType;
};

export function mapViewerOptions(params: MapViewerOptionsParamsType, customizationAssets: CustomizationAssets | null): ViewerOptions {
  const notNullGroups = params.groups ? params.groups : [];
  const camerasGroup = filterCamerasGroup(notNullGroups);
  return {
    groups: [
      ...mapGroups(
        notNullGroups.filter((group) => group.id !== camerasGroup?.id),
        customizationAssets
      ),
      ...mapCustomization(params.customization),
    ],
    cameras: mapCameras(camerasGroup),
  };
}

export function getSelectedItem(
  args: ViewerSelectionItemArgs,
  options: ViewerOptions
):
  | {
      group: ViewerGroupInterface;
      step: ViewerItemInterface | null;
    }
  | {
      group: ViewerGroupInterface;
      attribute: ViewerItemInterface | null;
    }
  | null {
  if (!!args?.groups?.length) {
    const selectedGroup = args?.groups[0];
    const group = options.groups.find((grp) => grp.externalCode === selectedGroup.groupId);
    if (group) {
      if (instanceOfViewerGroupStep(group)) {
        return {
          group,
          step: getSelectItem(selectedGroup.visibleAttributes, group.steps),
        };
      }
      if (instanceOfViewerGroupAttribute(group)) {
        return {
          group,
          attribute: getSelectItem(selectedGroup.visibleAttributes, group.attributes),
        };
      }
    }
  }
  return null;
}

function getSelectItem(selectedOptions: number[], options: ViewerItemInterface[]): ViewerItemInterface | null {
  const selectedId = !!selectedOptions?.length ? selectedOptions[0] : null;
  return selectedId ? options?.find((opt) => opt.externalCode === selectedId) || null : null;
}

function mapCameras(camerasGroup: Group | null): CameraInterface[] {
  return camerasGroup?.attributes.map((attr) => mapCameraOptions(attr.options)).reduce((prev, curr) => [...curr, ...prev], []) || [];
}

function mapGroups(groups: Group[], customizationAssets: CustomizationAssets | null): ViewerGroupInterface[] {
  return groups.map(({ id: externalCode, name, imageUrl, cameraLocationId, displayOrder, enabled, attributes }) => {
    return {
      id: getGroupCode(name),
      externalCode,
      imageUrl: imageUrl || null,
      cameraLocationId,
      displayOrder,
      enabled,
      ...mapAttributes(attributes, customizationAssets),
    };
  });
}

function mapAttributes(
  attributes: Attribute[],
  customizationAssets: CustomizationAssets | null
): { steps: ViewerItemInterface[] } | { attributes: ViewerItemInterface[] } {
  let resultProp: 'steps' | 'attributes' = 'attributes';

  const attributesList = attributes
    .filter((attr) => attr.enabled)
    .map(({ id, name, imageUrl, displayOrder, cameraLocationId, enabled, options, groupId }, index) => {
      const optionsResult = mapAttributeOptions(options, customizationAssets, groupId);
      if (index === 0 && 'attributes' in optionsResult) {
        resultProp = 'steps';
      }
      if (index !== 0 && ((resultProp === 'attributes' && 'attributes' in optionsResult) || (resultProp === 'steps' && 'options' in optionsResult))) {
        throw new Error(`The group must be in the attribute's format or in the steps format not both`);
      }

      return {
        id: getAttributeCode(name).id,
        name: getAttributeCode(name).name,
        externalCode: id,
        imageUrl,
        cameraLocationId,
        displayOrder,
        enabled,
        ...optionsResult,
      };
    });

  return { [resultProp]: attributesList };
}

function mapAttributeOptions(
  options: Option[],
  customizationAssets: CustomizationAssets | null,
  groupId: number
): { options: ViewerItemInterface[] } | { attributes: ViewerItemInterface[] } {
  const EMPTY_KEY = '_empty_';
  const attributes = groupBy(options || [], (opt) => {
    const keys = getOptionCode(opt.name);
    return keys.attributeCode || EMPTY_KEY;
  });

  const result: ViewerAttributeInterface[] = Object.entries(attributes).map(([stepKey, options], index) => ({
    id: stepKey,
    externalCode: index,
    displayOrder: index,
    imageUrl: null,
    enabled: true,
    cameraLocationId: null,
    groupId,
    options: mapOptions(options, customizationAssets),
  }));

  return Object.keys(attributes).length === 0
    ? { options: [] }
    : Object.keys(attributes).length === 1 && EMPTY_KEY in attributes
    ? { options: result[0].options }
    : { attributes: result };
}

function mapOptions(options: Option[], customizationAssets: CustomizationAssets | null): ViewerOptionInterface[] {
  return options
    .filter((item) => item.enabled)
    .map((item) =>
      mapOption(item, getOptionCode(item.name).optionCode, customizationAssets, getOptionCode(item.name).name, getOptionCode(item.name).reference)
    );
}

function mapOption(
  item: Option,
  id: string,
  customizationAssets: CustomizationAssets | null,
  name?: string | null,
  reference?: string | null
): ViewerOptionInterface {
  return {
    id,
    externalCode: item.id,
    imageUrl: item.imageUrl || null,
    missingImage:
      (customizationAssets && item.selected ? customizationAssets?.optionMissingImageSelected : customizationAssets?.optionMissingImage) || null,
    displayOrder: item.displayOrder,
    selected: item.selected,
    enabled: item.enabled,
    name: name || null,
    reference: reference || null,
    pricings: item.pricings,
  };
}

function mapCameraOptions(options: Option[]): ViewerOptionInterface[] {
  return options.map((item) => mapOption(item, item.name, null));
}

function filterCamerasGroup(groups: Group[]): Group | null {
  return groups.find((group) => group.attributes.findIndex((attr) => attr.code === FUTURE_FASHION_CONSTANTS.CAMERA_ATTRIBUTE_CODE) !== -1) || null;
}

function mapCustomization(params: MapViewerOptionsCustomizationParamsType): ViewerGroupCustomizationInterface[] {
  return !params || !params.name || !params.productAreas.length
    ? []
    : [
        {
          id: getGroupCode(params.name),
          externalCode: 1000,
          imageUrl: null,
          cameraLocationId: null,
          displayOrder: 1000,
          enabled: true,
          customizations: [],
          areas: params?.currentTemplate?.areas ? mapCustomizationChildren(params.items, params?.currentTemplate?.areas) : [],
          productAreas: params.productAreas,
          items: params.items,
          currentTemplate: params.currentTemplate,
        },
      ];
}

function mapCustomizationChildren(items: TextItem[] | null, areas: TemplateArea[]): ViewerTemplateAreaInterface[] {
  return areas.map((area) => {
    return {
      id: area.id,
      uploadRestrictions: area.uploadRestrictions,
      disableSellerImages: area.disableSellerImages,
      canAddImage: area.canAddImage,
      canAddText: area.canAddText,
      items: mapCustomizationAreaItem(items, area),
    };
  });
}

function mapCustomizationAreaItem(items: TextItem[] | ImageItem[] | null, area: TemplateArea): any | null {
  return items?.map((item) => {
    if (item.areaId === area.id) {
      if (item instanceof ImageItem) {
        return {
          id: item.guid,
          externalCode: item.guid,
          imageUrl: item.url,
          cameraLocationId: null,
          displayOrder: 1000,
          enabled: true,
          name: null,
          font: null,
          bold: null,
          italic: null,
        };
      } else if (item instanceof TextItem) {
        return {
          id: item.guid,
          externalCode: item.guid,
          imageUrl: null,
          cameraLocationId: null,
          displayOrder: 1000,
          enabled: true,
          name: item.text,
          font: item.fontFamily,
          bold: item.fontWeight.includes('bold') ? true : false,
          italic: item.fontWeight.includes('italic') ? true : false,
        };
      } else {
        return null;
      }
    }
  });
}

function getGroupCode(value: string): string {
  const splitValue = value.split(CODE_SPLIT_CHAR.CODE_NAME);
  const codes = CODE_SECTION.GROUP;

  if (splitValue.length > 1 && codes.some((code) => splitValue[0].startsWith(code))) return splitValue[0];

  return value;
}

function getAttributeCode(value: string): { id: string; name: string | null } {
  const splitValue = value.split(CODE_SPLIT_CHAR.CODE_NAME);
  const codes = CODE_SECTION.ATTRIBUTE;
  let id: string = '';
  let name: string | null = null;
  if (splitValue.length > 1 && codes.some((code) => splitValue[0].startsWith(code))) {
    id = splitValue[0];
    if (splitValue[1]) {
      name = splitValue[1];
    }
    return {
      id,
      name,
    };
  }

  return {
    id: value,
    name: null,
  };
}

function getOptionCode(value: string): { optionCode: string; attributeCode: string | null; name: string | null; reference: string | null } {
  const splitValue = value.split(CODE_SPLIT_CHAR.CODE_NAME);

  const attributesCodes = CODE_SECTION.ATTRIBUTE;
  const optionsCodes = CODE_SECTION.OPTION;
  let name: string | null = null;
  let reference: string | null = null;
  if (splitValue.length > 1) {
    const attributeOptionCodes = splitValue[0];
    const splitAttributeOptionCodes = attributeOptionCodes.split(CODE_SPLIT_CHAR.ATTRIBUTE_OPTION);
    const attributeCode = splitAttributeOptionCodes && splitAttributeOptionCodes.length > 1 ? splitAttributeOptionCodes[0] : null;
    const optionCode =
      splitAttributeOptionCodes && splitAttributeOptionCodes.length > 1 ? splitAttributeOptionCodes[1] : splitAttributeOptionCodes[0];

    if (splitValue[1]) name = splitValue[1];
    if (splitValue[2]) reference = splitValue[2];
    if (
      (!attributeCode || attributesCodes.some((code) => attributeCode.startsWith(code))) &&
      optionsCodes.some((code) => optionCode.startsWith(code))
    ) {
      return {
        optionCode,
        attributeCode,
        name,
        reference,
      };
    }
  }

  return {
    optionCode: value,
    attributeCode: null,
    name,
    reference,
  };
}

export function instanceOfViewerGroupAttribute(obj: ViewerGroupInterface): obj is ViewerGroupAttributeInterface {
  return 'attributes' in obj;
}
export function instanceOfViewerGroupStep(obj: ViewerGroupInterface): obj is ViewerGroupStepInterface {
  return 'steps' in obj;
}

export function instanceOfViewerGroupCustomizations(obj: ViewerGroupInterface): obj is ViewerGroupCustomizationInterface {
  return 'customizations' in obj;
}

export function instanceOfViewerGroupInput(obj: ViewerGroupInterface): obj is ViewerGroupInputItemsInterface {
  return 'categoryOptions' in obj;
}
export function instaneOfViewerOptionInput(obj: ViewerOptionInterface): obj is ViewerOptionInterfaceInput {
  return 'value' in obj;
}
